import { FC } from "preact/compat";
import { useComputed, useSignal } from "@preact/signals";
import { Highlight, Table, TextField } from "~/components";
import { Task } from "~/models";
import { projects } from "~/placeholder";
import SearchIcon from "@material-design-icons/svg/outlined/search.svg";

const INITIAL_ALL_TASKS = projects.flatMap((project) => project.tasks);

const taskMatches = (task: Task, search: string) =>
    task.name.toLowerCase().includes(search) ||
    task.description.toLowerCase().includes(search) ||
    task.project.name.toLowerCase().includes(search);

const TaskList: FC = () => {
    const tasks = useSignal(INITIAL_ALL_TASKS);
    const search = useSignal("");
    const filteredTasks = useComputed(() =>
        tasks.value.filter((task) =>
            taskMatches(task, search.value.toLowerCase()),
        ),
    );

    return (
        <div className="p-4">
            <div className="flex items-center pb-4">
                <TextField
                    className="mr-6 w-[240px]"
                    rightIcon={<SearchIcon />}
                    placeholder="Search..."
                    value={search.value}
                    onInput={(event) =>
                        (search.value = event.currentTarget.value)
                    }
                />
            </div>
            <div>
                <Table
                    className="w-full"
                    rows={filteredTasks.value}
                    cells={[
                        "project",
                        "name",
                        "status",
                        "description",
                        "resourceStatus",
                        "reporter",
                        "assignee",
                    ]}
                    header={{
                        project: "Project",
                        name: "Title",
                        status: "Status",
                        description: "Description",
                        resourceStatus: "Resource Status",
                        reporter: "Reporter",
                        assignee: "Assigned",
                    }}
                    renderers={{
                        project: (row) => (
                            <Highlight
                                value={row.project.name}
                                search={search.value}
                            />
                        ),
                        name: (row) => (
                            <Highlight value={row.name} search={search.value} />
                        ),
                        description: (row) => (
                            <Highlight
                                value={row.description}
                                search={search.value}
                            />
                        ),
                        resourceStatus: (row) => row.resourceStatus.name,
                        status: (row) => row.status.name,
                        reporter: (row) => row.reporter?.name || "-",
                        assignee: (row) => row.assignee?.name || "-",
                    }}
                />
                {search.value && filteredTasks.value.length === 0 && (
                    <div className="py-4 text-center text-sm text-gray-500">
                        No results found
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskList;
